import { useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { getOfficerPluralLabel } from '@/utils/auth';
import { KstDataGrid } from '@/components/KstDataGrid';
import { ConfirmModal } from '@/components/ConfirmModal';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';
import { EmailChipsInput } from '@/components/EmailChipsInput';
import { TimePicker } from '@/components/TimePicker';
import { DateTimePicker } from '@/components/DateTimePicker';
import { AdminTabBar } from '@/components/AdminTabBar';
import { useTourManagement } from './useTourManagement';
import {
  tourListColumns, tourListHeaderButtons,
  toursTourStopsColumns, toursTourStopsHeaderButtons,
  tourStopsColumns, tourStopHeaderButtons, tourConnectedStopsHeaderButtons,
  tourStopTasksColumns, tourStopTasksHeaderButtons,
  taskColumns, tasksHeaderButton, tasksGlobalHeaderButton,
  tourExemptionColumns, tourExemptionHeaderButtons,
  tourLogColumns,
  getGuardTourHistoryColumns, getGuardTourStopsForGuardTourColumns,
  mobileTabs,
} from './tour-management-config';

import { AssociatedStopsModal } from './tour-management/AssociatedStopsModal';
import { AssignToStopsModal } from './tour-management/AssignToStopsModal';
import { TourNextRunPreview } from './tour-management/TourNextRunPreview';
import moment from 'moment';
import { hasValue } from '@/utils/legacy-utils';

// Value lookups: access lazily at usage sites via (window as any).X

// enabledDaysOfWeek is a 7-bit mask using java.time.DayOfWeek numbering (Mon=bit0..Sun=bit6,
// bit = 1 << (value - 1)). This is DIFFERENT from the KSTDayOfWeek numbering used by the
// WeekDays constant (Sunday=1..Saturday=7), so the bits are defined explicitly BY NAME here —
// never derive them from WeekDays[i].dayOfWeek arithmetically. Display order is Mon..Sun.
const WEEK_DAYS: { name: string; short: string; bit: number }[] = [
  { name: 'Monday', short: 'Mo', bit: 1 },
  { name: 'Tuesday', short: 'Tu', bit: 2 },
  { name: 'Wednesday', short: 'We', bit: 4 },
  { name: 'Thursday', short: 'Th', bit: 8 },
  { name: 'Friday', short: 'Fr', bit: 16 },
  { name: 'Saturday', short: 'Sa', bit: 32 },
  { name: 'Sunday', short: 'Su', bit: 64 },
];
const DAY_MASK_ALL = 127;
const DAY_MASK_WEEKDAYS = 31;
const DAY_MASK_WEEKENDS = 96;

// ── SVG Icons (inlined from TourManagement.html) ─────────────────────

const ToursIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.9641 7H10.9641V9H18.9641V7Z" fill="currentColor" />
    <path d="M6 8.82864V15.1714L9.9642 12L6 8.82864Z" fill="currentColor" />
    <path d="M18.9641 11H10.9641V13H18.9641V11Z" fill="currentColor" />
    <path d="M10.9641 15H18.9641V17H10.9641V15Z" fill="currentColor" />
  </svg>
);
const StopsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
  </svg>
);
const TasksIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
  </svg>
);
const ExceptionsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
  </svg>
);
const LogsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const HistoryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" fill="currentColor"/>
  </svg>
);
const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ClockIcon = () => (
  <svg className="kst-availability-empty-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ── Selectable officer comments options ──────────────────────────────

const SelectableAreOfficerCommentsRequired = [
  { code: 1, value: 'Required' },
  { code: 2, value: 'Optional' },
  { code: 3, value: 'Disabled' },
];

// ── Component ────────────────────────────────────────────────────────

export function TourManagementScreen({ initialSubTab }: { initialSubTab?: string }) {
  const { theme } = useTheme();
  const h = useTourManagement(initialSubTab);
  const officersLabel = useMemo(() => getOfficerPluralLabel().toLowerCase(), []);

  // Memoize guard tour history columns (they need callbacks)
  const guardTourHistoryColumns = useMemo(
    () => getGuardTourHistoryColumns(h.showGuardTourStopsForGuardTour),
    [h.showGuardTourStopsForGuardTour]
  );
  const guardTourStopsForGuardTourColumns = useMemo(
    () => getGuardTourStopsForGuardTourColumns(h.showGuardTourStop),
    [h.showGuardTourStop]
  );

  return (
    <div className={theme} id="TourManagementSection">
      {/* Delete confirmation modal */}
      <ConfirmModal
        open={h.deleteConfirm.open}
        title={h.deleteConfirm.title}
        message={h.deleteConfirm.message}
        confirmText="Delete"
        cancelText="Cancel"
        confirmDisabled={h.isDeleting}
        busy={h.isDeleting}
        theme={theme}
        onConfirm={h.handleConfirmDelete}
        onCancel={h.handleCancelDelete}
      />

      {/* Copy tour modal */}
      <BaseFormModal
        open={h.copyTourModal.open}
        title="Copy Tour"
        saveText="Copy"
        cancelText="Cancel"
        theme={theme}
        onSave={h.handleConfirmCopyTour}
        onCancel={h.handleCancelCopyTour}
      >
        <div className="kstc-radio-group">
          <label className="kstc-radio-label">
            <input
              type="radio"
              name="copy_tour_mode"
              checked={h.copyTourModal.withStops}
              onChange={() => h.handleCopyTourWithStopsChange(true)}
            />
            <span>Copy with tour stops</span>
          </label>
          <label className="kstc-radio-label">
            <input
              type="radio"
              name="copy_tour_mode"
              checked={!h.copyTourModal.withStops}
              onChange={() => h.handleCopyTourWithStopsChange(false)}
            />
            <span>Copy without tour stops</span>
          </label>
        </div>
      </BaseFormModal>

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar
            tabs={mobileTabs}
            activeTab={h.activeTabString}
            onTabChange={h.onMobileTabChange}
          />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li className={`kst-sidebar-item${h.activeTabString === 'showToursTab' ? ' kst-sidebar-item--active' : ''}`} onClick={h.showTourManagement}>
                <ToursIcon /><span className="kst-sidebar-text">Tours</span>
              </li>
              <li className={`kst-sidebar-item${h.activeTabString === 'showTourStopsTab' ? ' kst-sidebar-item--active' : ''}`} onClick={h.showTourStopList}>
                <StopsIcon /><span className="kst-sidebar-text">Stops</span>
              </li>
              <li className={`kst-sidebar-item${h.activeTabString === 'showTourStopTasksTab' ? ' kst-sidebar-item--active' : ''}`} onClick={h.showTaskList}>
                <TasksIcon /><span className="kst-sidebar-text">Tasks</span>
              </li>
              <li className={`kst-sidebar-item${h.activeTabString === 'showExemptionsTab' ? ' kst-sidebar-item--active' : ''}`} onClick={h.showExemptionList}>
                <ExceptionsIcon /><span className="kst-sidebar-text">Exceptions</span>
              </li>
              <li className={`kst-sidebar-item${h.activeTabString === 'showLogsTab' ? ' kst-sidebar-item--active' : ''}`} onClick={h.showAllTourLogs}>
                <LogsIcon /><span className="kst-sidebar-text">Logs</span>
              </li>
              <li className={`kst-sidebar-item${h.activeTabString === 'showGuardTourHistoryTab' ? ' kst-sidebar-item--active' : ''}`} onClick={h.showGuardTourHistory}>
                <HistoryIcon /><span className="kst-sidebar-text">History</span>
              </li>
              <li className={`kst-sidebar-item${h.activeTabString === 'showRemindersTab' ? ' kst-sidebar-item--active' : ''}`} onClick={h.showReminders}>
                <LogsIcon /><span className="kst-sidebar-text">Reminders</span>
              </li>
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">

            {/* ═══ Tour Management Page (Main List) ═══ */}
            <div style={{ display: h.pageShowString === 'showManagement' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <div>
                  <h3 className="kst-page-title">Tour Management</h3>
                  <p className="kst-page-description">Tours allow you to create a collection of stops for a specific route or shift.</p>
                </div>
              </div>
              <KstDataGrid
                columns={tourListColumns}
                rows={h.rows}
                loading={h.toursLoading}
                pager={h.tourListPager}
                headerbuttons={tourListHeaderButtons}
                showcheckbox={true}
                showstatustoggle={true}
                mobiletitlefield="title"
                onGoToPage={h.loadTours}
                onButtonCommand={h.handleTourListCommand}
                onStatusSwitch={h.handleSwitchTourStatus}
              />
            </div>

            {/* ═══ Edit Tour Page ═══ */}
            <div style={{ display: h.pageShowString === 'showTour' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <div>
                  <h3 className="kst-page-title">{h.currentTour.title || 'New Tour'}</h3>
                  <p className="kst-page-description">{h.currentTour.type}</p>
                </div>
              </div>

              {/* Tour Sub-tabs */}
              <div className="kst-sub-tabs">
                <button type="button" className={`kst-sub-tab${h.tourSubTab === 'tour' ? ' kst-sub-tab--active' : ''}`} onClick={() => h.setTourSubTab('tour')}>Tour</button>
                <button type="button" className={`kst-sub-tab${h.tourSubTab === 'tourStopList' ? ' kst-sub-tab--active' : ''}`} onClick={() => h.setTourSubTab('tourStopList')}>Tour Stop List</button>
              </div>

              {/* Tour Details Sub-tab */}
              <div style={{ display: h.tourSubTab === 'tour' ? '' : 'none' }} className="kst-form-section">
                <div className="kst-form-card">
                  <div className="kst-form-card-header">{h.currentTour.title || 'Tour Details'}</div>
                  <div className="kst-form-card-body">
                    <div className="kstc-form-group">
                      <label className="kstc-label kstc-label--required">Time Zone</label>
                      <Dropdownlist
                        value={h.currentTour.timeZoneId}
                        items={(window as any).SelectableTimeZones}
                        labelKey="code"
                        valueKey="code"
                        placeholder="Select Time Zone"
                        sortItems={false}
                        onChange={(val) => h.setCurrentTour((prev: any) => ({ ...prev, timeZoneId: val }))}
                      />
                    </div>
                    <div className="kstc-form-group">
                      <label className="kstc-label kstc-label--required">Tour Title</label>
                      <input type="text" className="kstc-input" maxLength={255} placeholder="Tour Title" value={h.currentTour.title || ''} onChange={(e) => h.setCurrentTour((prev: any) => ({ ...prev, title: e.target.value }))} />
                    </div>
                    <div className="kstc-form-group">
                      <label className="kstc-label kstc-label--required">Tour Type</label>
                      <input type="text" className="kstc-input" maxLength={255} placeholder="Tour Type" value={h.currentTour.type || ''} onChange={(e) => h.setCurrentTour((prev: any) => ({ ...prev, type: e.target.value }))} />
                    </div>
                    <div className="kstc-form-group">
                      <label className="kstc-label kstc-label--required">Status</label>
                      <div>
                        {!h.currentTour.isEnabled && (
                          <button type="button" className="kstc-btn kstc-btn--danger kstc-btn--sm" onClick={() => h.setCurrentTour((prev: any) => ({ ...prev, isEnabled: true }))}>Disabled</button>
                        )}
                        {h.currentTour.isEnabled && (
                          <button type="button" className="kstc-btn kstc-btn--success kstc-btn--sm" onClick={() => h.setCurrentTour((prev: any) => ({ ...prev, isEnabled: false }))}>Enabled</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="kst-form-card">
                  <div className="kst-form-card-header">Available Hours</div>
                  <div className="kst-form-card-body">
                    <p className="kst-availability-description">Define when this tour is available. Each time block specifies a start and end period.</p>
                    <div className="kst-availability-list">
                      {(!h.currentTour.tourAvailabilityList || h.currentTour.tourAvailabilityList.length === 0) && (
                        <div className="kst-availability-empty">
                          <ClockIcon />
                          <p>No availability periods defined</p>
                          <span>Click "Add Time Block" to define when this tour is available</span>
                        </div>
                      )}
                      {(h.currentTour.tourAvailabilityList || []).map((group: any, index: number) => (
                        <div key={group.guid} className="kst-availability-card">
                          <div className="kst-availability-card-header">
                            <span className="kst-availability-card-title">Time Block {index + 1}</span>
                            <button type="button" className="kst-availability-card-remove" onClick={() => h.handleRemoveTourDay(group.guid)} title="Remove this time block">
                              <CloseIcon />
                            </button>
                          </div>
                          <div className="kst-availability-card-body">
                            <div className="kst-availability-period">
                              <div className="kst-availability-period-label">From</div>
                              <div className="kst-availability-period-fields">
                                <Dropdownlist
                                  value={group.startDayOfWeek}
                                  items={(window as any).WeekDays}
                                  labelKey="value"
                                  valueKey="dayOfWeek"
                                  placeholder="Day"
                                  sortItems={false}
                                  searchable={false}
                                  onChange={(val) => { group.startDayOfWeek = val; h.setCurrentTour((prev: any) => ({ ...prev })); }}
                                />
                                <span className="kst-availability-at">at</span>
                                <TimePicker
                                  value={group.startTimeDisplay ?? ''}
                                  placeholder="Time"
                                  onChange={(val) => { group.startTimeDisplay = val; h.setCurrentTour((prev: any) => ({ ...prev })); }}
                                />
                              </div>
                            </div>
                            <div className="kst-availability-arrow"><ArrowRightIcon /></div>
                            <div className="kst-availability-period">
                              <div className="kst-availability-period-label">To</div>
                              <div className="kst-availability-period-fields">
                                <Dropdownlist
                                  value={group.endDayOfWeek}
                                  items={(window as any).WeekDays}
                                  labelKey="value"
                                  valueKey="dayOfWeek"
                                  placeholder="Day"
                                  sortItems={false}
                                  searchable={false}
                                  onChange={(val) => { group.endDayOfWeek = val; h.setCurrentTour((prev: any) => ({ ...prev })); }}
                                />
                                <span className="kst-availability-at">at</span>
                                <TimePicker
                                  value={group.endTimeDisplay ?? ''}
                                  placeholder="Time"
                                  onChange={(val) => { group.endTimeDisplay = val; h.setCurrentTour((prev: any) => ({ ...prev })); }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="kst-availability-add" onClick={h.handleAddTourDay}>
                      <PlusIcon /> Add Time Block
                    </button>
                    {/* While editing a saved tour, h.rows holds its attached stops
                        (loaded by handleEditTour); the count is unknown for a new
                        unsaved tour or while the grid is loading/repurposed for the
                        add-global-stops picker. */}
                    <TourNextRunPreview
                      tour={h.currentTour}
                      stopCount={
                        h.currentTour.id != null && h.showAllTourStopsForTour && !h.tourStopsLoading
                          ? (h.rows.entries ? h.rows.entries.length : null)
                          : null
                      }
                    />
                    <div className="kst-form-actions-wrapper">
                      <div className="kst-form-actions">
                        <button type="button" className="kstc-btn kstc-btn--primary" onClick={h.handleSaveTour}
                          disabled={!h.showAllTourStopsForTour}
                          title={!h.showAllTourStopsForTour ? 'Add or cancel selected tour stops first' : ''}>Save Changes</button>
                        {h.currentTour.id != null && (
                          <button type="button" className="kstc-btn kstc-btn--secondary" onClick={h.handleCopyTour}>Copy Tour</button>
                        )}
                        <button type="button" className="kstc-btn kstc-btn--secondary" onClick={h.handleCloseTour}>Close</button>
                      </div>
                      {!h.showAllTourStopsForTour && <span className="kst-save-hint">Add or cancel selected tour stops first</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tour Stop List Sub-tab */}
              <div style={{ display: h.tourSubTab === 'tourStopList' ? '' : 'none' }}>
                {h.currentTour.id == null && (
                  <div className="kst-empty-state"><h3>Tour must be saved before adding tour stops.</h3></div>
                )}
                {h.showAllTourStopsForTour && h.currentTour.id != null && (
                  <KstDataGrid
                    columns={toursTourStopsColumns}
                    rows={h.rows}
                    loading={h.tourStopsLoading}
                    pager={h.tourStopsForTourPager}
                    headerbuttons={toursTourStopsHeaderButtons}
                    showcheckbox={true}
                    showordercolumn={true}
                    showstatustoggle={true}
                    mobiletitlefield="title"
                    onButtonCommand={h.handleTourStopsForTourCommand}
                    onStatusSwitch={h.handleSwitchTourStopStatus}
                    onRowMoved={h.handleReorderStopMoved}
                  />
                )}
                {!h.showAllTourStopsForTour && (
                  <div>
                    <div className="kst-page-header"><h4 className="kst-page-title">Global Tour Stops</h4></div>
                    <KstDataGrid
                      columns={tourStopsColumns}
                      rows={h.rows}
                      loading={h.tourStopsLoading}
                      pager={h.tourStopsPager}
                      headerbuttons={tourConnectedStopsHeaderButtons}
                      showcheckbox={true}
                      showstatustoggle={true}
                      mobiletitlefield="title"
                      onButtonCommand={h.handleGlobalTourStopsCommand}
                      onStatusSwitch={h.handleSwitchTourStopStatus}
                      onGoToPage={h.loadTourStops}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ═══ Tour Stop List Page ═══ */}
            <div style={{ display: h.pageShowString === 'showTourStopList' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <div>
                  <h3 className="kst-page-title">Tour Stops</h3>
                  <p className="kst-page-description">Tour Stops are a specific location that an employee needs to attend to.</p>
                </div>
              </div>
              <KstDataGrid
                columns={tourStopsColumns}
                rows={h.rows}
                loading={h.tourStopsLoading}
                pager={h.tourStopsPager}
                headerbuttons={tourStopHeaderButtons}
                showcheckbox={true}
                showstatustoggle={true}
                mobiletitlefield="title"
                onGoToPage={h.loadTourStops}
                onButtonCommand={h.handleTourStopListCommand}
                onStatusSwitch={h.handleSwitchTourStopStatus}
              />
            </div>

            {/* ═══ Edit Tour Stop Page ═══ */}
            <div style={{ display: (h.pageShowString === 'showTourStop' || h.pageShowString === 'showTourStopForTour') ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">{h.currentTourStop.title || 'New Tour Stop'}</h3>
                <div className="kst-page-actions-wrapper">
                  <div className="kst-page-actions">
                    <button type="button" id="btnTourManagementSaveTourStop" className="kstc-btn kstc-btn--success" onClick={h.handleSaveTourStop}
                      disabled={h.tourStopSubTab === 'taskList' && !h.showGlobalTasksForTourStop}
                      title={h.tourStopSubTab === 'taskList' && !h.showGlobalTasksForTourStop ? 'Add or cancel selected tasks first' : ''}>Save</button>
                    <button type="button" className="kstc-btn kstc-btn--danger" onClick={h.handleCloseTourStop}>Close</button>
                  </div>
                  {h.tourStopSubTab === 'taskList' && !h.showGlobalTasksForTourStop && (
                    <span className="kst-save-hint">Add or cancel selected tasks first</span>
                  )}
                </div>
              </div>

              {/* Tour Stop Sub-tabs */}
              <div className="kst-sub-tabs">
                <button type="button" className={`kst-sub-tab${h.tourStopSubTab === 'details' ? ' kst-sub-tab--active' : ''}`} onClick={() => h.setTourStopSubTab('details')}>Details</button>
                <button type="button" className={`kst-sub-tab${h.tourStopSubTab === 'taskList' ? ' kst-sub-tab--active' : ''}`} onClick={() => h.setTourStopSubTab('taskList')}>Task List</button>
                <button type="button" className={`kst-sub-tab${h.tourStopSubTab === 'issues' ? ' kst-sub-tab--active' : ''}`} onClick={() => h.setTourStopSubTab('issues')}>Issues</button>
                <button type="button" className={`kst-sub-tab${h.tourStopSubTab === 'geofence' ? ' kst-sub-tab--active' : ''}`} onClick={() => { h.setTourStopSubTab('geofence'); h.handleGeoFenceTabClicked(); }}>Geo-Fence</button>
                <button type="button" className={`kst-sub-tab${h.tourStopSubTab === 'advanced' ? ' kst-sub-tab--active' : ''}`} onClick={() => h.setTourStopSubTab('advanced')}>Advanced</button>
              </div>

              {/* Details Sub-tab */}
              <div style={{ display: h.tourStopSubTab === 'details' ? '' : 'none' }} className="kst-form-section">
                <p className="kst-required-note"><span>*</span> Required field</p>
                <div className="kst-form-row">
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label kstc-label--required" htmlFor="title">Title</label>
                    <input type="text" id="title" className="kstc-input" maxLength={255} value={h.currentTourStop.title || ''} onChange={(e) => h.setCurrentTourStop((prev: any) => ({ ...prev, title: e.target.value }))} />
                  </div>
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label kstc-label--required">Number of Times to Complete</label>
                    <input type="number" className="kstc-input" min={1} max={1000} value={h.currentTourStop.numTimesToComplete || ''} onChange={(e) => h.setCurrentTourStop((prev: any) => ({ ...prev, numTimesToComplete: parseInt(e.target.value) || null }))} />
                  </div>
                </div>
                <div className="kst-form-row">
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label kstc-label--required">Client</label>
                    <Dropdownlist
                      value={h.currentTourStop.client?.id}
                      items={h.npsdClientList}
                      labelKey="name"
                      valueKey="id"
                      placeholder="Select Client"
                      onChange={(val) => {
                        h.setCurrentTourStop((prev: any) => ({ ...prev, client: { ...prev.client, id: val } }));
                        h.handleShowSitesForClient(val as number);
                      }}
                    />
                  </div>
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label">Site</label>
                    {h.selectableClientSites.length > 0 ? (
                      <MultiSelect
                        value={h.currentTourStop.client?.sites || []}
                        items={h.selectableClientSites}
                        labelKey="siteName"
                        valueKey="id"
                        placeholder="Select sites"
                        onChange={h.handleOnSitesChanged}
                      />
                    ) : (
                      <div className="kst-empty-hint">Select a client to see available sites</div>
                    )}
                  </div>
                </div>
                <div className="kst-form-row">
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label kstc-label--required">Start Date</label>
                    <DateTimePicker
                      value={h.currentTourStop.startDateTime ? new Date(h.currentTourStop.startDateTime) : null}
                      type="datetime"
                      format="DD MMM YYYY HH:mm"
                      valueType="date"
                      clearable={true}
                      appendToBody={true}
                      timeZoneId={h.currentTourStop.timeZoneId}
                      onChange={(val) => h.setCurrentTourStop((prev: any) => ({ ...prev, startDateTime: val }))}
                    />
                    <span className="kst-field-hint">Time is shown in the stop's time zone.</span>
                  </div>
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label">End Date</label>
                    <DateTimePicker
                      value={h.currentTourStop.endDateTime ? new Date(h.currentTourStop.endDateTime) : null}
                      type="datetime"
                      format="DD MMM YYYY HH:mm"
                      valueType="date"
                      clearable={true}
                      appendToBody={true}
                      timeZoneId={h.currentTourStop.timeZoneId}
                      onChange={(val) => h.setCurrentTourStop((prev: any) => ({ ...prev, endDateTime: val }))}
                    />
                    <span className="kst-field-hint">Time is shown in the stop's time zone. Leave empty to run indefinitely.</span>
                  </div>
                </div>
                <div className="kst-form-row">
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label kstc-label--required" htmlFor="txtValidateableLocation">Location</label>
                    <input type="text" className="kstc-input" maxLength={255} id="txtValidateableLocation" placeholder="Address, Intersection or Coordinates" value={h.currentTourStop.location || ''} onChange={(e) => h.setCurrentTourStop((prev: any) => ({ ...prev, location: e.target.value }))} onBlur={h.handleTourStopLocationBlurred} />
                    {h.isShowingClientAddressButton && (
                      <button type="button" id="btnTourManagementTourStopClientAddress" onClick={h.handleUpdateClientAddressFromServer} className="kstc-btn kstc-btn--link kstc-btn--sm" title="Click 'Client Address' to use your clients location as the call location.">Client Address</button>
                    )}
                  </div>
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label">Time Zone</label>
                    <Dropdownlist
                      value={h.currentTourStop.timeZoneId}
                      items={(window as any).SelectableTimeZones}
                      labelKey="code"
                      valueKey="code"
                      placeholder="Select Time Zone"
                      sortItems={false}
                      onChange={(val) => h.setCurrentTourStop((prev: any) => ({ ...prev, timeZoneId: val }))}
                    />
                  </div>
                </div>
                <div className="kst-form-row">
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label">{h.officerLabel} Comments</label>
                    <Dropdownlist
                      value={h.currentTourStop.areOfficerCommentsRequired}
                      items={SelectableAreOfficerCommentsRequired}
                      labelKey="value"
                      valueKey="code"
                      placeholder="Select Option"
                      sortItems={false}
                      searchable={false}
                      onChange={(val) => h.setCurrentTourStop((prev: any) => ({ ...prev, areOfficerCommentsRequired: val }))}
                    />
                  </div>
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label">Allow Uploads?</label>
                    <label className="kst-toggle">
                      <input type="checkbox" className="kst-toggle__input" checked={h.currentTourStop.areOfficerAttachmentsEnabled || false} onChange={(e) => h.setCurrentTourStop((prev: any) => ({ ...prev, areOfficerAttachmentsEnabled: e.target.checked }))} />
                      <span className="kst-toggle__switch"></span>
                      <span className={`kst-toggle__label ${h.currentTourStop.areOfficerAttachmentsEnabled ? 'kst-toggle__label--on' : 'kst-toggle__label--off'}`}>
                        {h.currentTourStop.areOfficerAttachmentsEnabled ? 'Yes' : 'No'}
                      </span>
                    </label>
                  </div>
                </div>
                <div className="kstc-form-group">
                  <label className="kstc-label kstc-label--required" htmlFor="taskStopInstructions">Instructions</label>
                  <textarea id="taskStopInstructions" className="kstc-textarea" rows={10} value={h.currentTourStop.instructions || ''} onChange={(e) => h.setCurrentTourStop((prev: any) => ({ ...prev, instructions: e.target.value }))} />
                </div>
                <div className="kst-form-row">
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label">Pause Window Start</label>
                    <DateTimePicker
                      value={h.currentTourStop.pauseWindowStartDateTime ? new Date(h.currentTourStop.pauseWindowStartDateTime) : null}
                      type="datetime"
                      format="DD MMM YYYY HH:mm"
                      valueType="date"
                      clearable={true}
                      appendToBody={true}
                      timeZoneId={h.currentTourStop.timeZoneId}
                      onChange={(val) => h.setCurrentTourStop((prev: any) => ({ ...prev, pauseWindowStartDateTime: val }))}
                    />
                    <span className="kst-field-hint">During the pause window the stop is hidden from officers as completable. Leave empty for no pause.</span>
                  </div>
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label">Pause Window End</label>
                    <DateTimePicker
                      value={h.currentTourStop.pauseWindowEndDateTime ? new Date(h.currentTourStop.pauseWindowEndDateTime) : null}
                      type="datetime"
                      format="DD MMM YYYY HH:mm"
                      valueType="date"
                      clearable={true}
                      appendToBody={true}
                      timeZoneId={h.currentTourStop.timeZoneId}
                      onChange={(val) => h.setCurrentTourStop((prev: any) => ({ ...prev, pauseWindowEndDateTime: val }))}
                    />
                    <span className="kst-field-hint">Stop reactivates automatically at this time.</span>
                  </div>
                </div>
                <div className="kstc-form-group">
                  <button
                    type="button"
                    className="kstc-btn kstc-btn--link kstc-btn--sm"
                    onClick={() => h.setCurrentTourStop((prev: any) => ({ ...prev, pauseWindowStartDateTime: null, pauseWindowEndDateTime: null }))}
                    disabled={!h.currentTourStop.pauseWindowStartDateTime && !h.currentTourStop.pauseWindowEndDateTime}
                  >
                    Clear pause window
                  </button>
                </div>
                <div className="kstc-form-group">
                  <label className="kstc-label">End-of-stop reminder last sent (this stop)</label>
                  <div className="kst-reminder-email-row">
                    <input
                      type="text"
                      className="kstc-input"
                      readOnly
                      disabled
                      value={h.currentTourStop.lastReminderSentAt
                        ? new Date(h.currentTourStop.lastReminderSentAt).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            timeZoneName: 'short',
                          })
                        : 'Not yet sent'}
                    />
                    <small className="kstc-help-text">
                      Recipient is configured under the Reminders tab and applies to all tour stops in your company.
                    </small>
                  </div>
                </div>
              </div>

              {/* Task List Sub-tab */}
              <div style={{ display: h.tourStopSubTab === 'taskList' ? '' : 'none' }}>
                <div className="kst-page-description">
                  <h4>What are Tasks?</h4>
                  <p>Tasks allow for management to apply specific items that need checking at a location. This helps guide your {officersLabel} to perform the correct task and on time.</p>
                </div>
                {h.showGlobalTasksForTourStop ? (
                  <KstDataGrid
                    columns={tourStopTasksColumns}
                    rows={h.tourStopTaskRows}
                    pager={h.tourStopTasksPager}
                    headerbuttons={tourStopTasksHeaderButtons}
                    mobiletitlefield="name"
                    showcheckbox={true}
                    showordercolumn={true}
                    onButtonCommand={h.handleTourStopTasksCommand}
                    onRowMoved={h.handleReorderTaskMoved}
                  />
                ) : (
                  <KstDataGrid
                    columns={taskColumns}
                    rows={h.rows}
                    loading={h.tasksLoading}
                    pager={h.tasksPager}
                    headerbuttons={tasksGlobalHeaderButton}
                    mobiletitlefield="name"
                    showcheckbox={true}
                    onButtonCommand={h.handleGlobalTasksCommand}
                    onGoToPage={h.loadTourTasks}
                  />
                )}
              </div>

              {/* Issues Sub-tab */}
              <div style={{ display: h.tourStopSubTab === 'issues' ? '' : 'none' }} className="kst-form-section">
                <h4>What are issues?</h4>
                <p>Issues allow for your {officersLabel} to capture photographs and attach them to the stop they are checking. All issues are visible by future {officersLabel} to avoid duplication documentation. {getOfficerPluralLabel()} can check these issues and confirm if they've been fixed or not resulting in a more efficient operation.</p>
                <div className="kstc-form-group">
                  <label className="kstc-label">Do you want to enable issues?</label>
                  <label className="kst-toggle">
                    <input type="checkbox" className="kst-toggle__input" checked={h.currentTourStop.areIssuesEnabled || false} onChange={(e) => h.setCurrentTourStop((prev: any) => ({ ...prev, areIssuesEnabled: e.target.checked }))} />
                    <span className="kst-toggle__switch"></span>
                    <span className={`kst-toggle__label ${h.currentTourStop.areIssuesEnabled ? 'kst-toggle__label--on' : 'kst-toggle__label--off'}`}>
                      {h.currentTourStop.areIssuesEnabled ? 'Yes' : 'No'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Geo-Fence Sub-tab */}
              <div style={{ display: h.tourStopSubTab === 'geofence' ? '' : 'none' }} className="kst-form-section">
                <h4>What is a Geo-Fence?</h4>
                <p>A Geo-Fence is a virtual perimeter around a set location. Enabling this virtual perimeter prevents {officersLabel} from completing tasks and stops while outside the perimeter.</p>
                <div className="kstc-form-group">
                  <label className="kstc-label">Do you want to enable Geo-Fence?</label>
                  <label className="kst-toggle">
                    <input type="checkbox" className="kst-toggle__input" checked={h.currentTourStop.isGeofenceEnabled || false} onChange={(e) => {
                      const val = e.target.checked;
                      h.setCurrentTourStop((prev: any) => ({ ...prev, isGeofenceEnabled: val }));
                      h.handleSwitchGeoFence(val);
                    }} />
                    <span className="kst-toggle__switch"></span>
                    <span className={`kst-toggle__label ${h.currentTourStop.isGeofenceEnabled ? 'kst-toggle__label--on' : 'kst-toggle__label--off'}`}>
                      {h.currentTourStop.isGeofenceEnabled ? 'Yes' : 'No'}
                    </span>
                  </label>
                </div>
                {h.currentTourStop.isGeofenceEnabled && (
                  <div className="kst-geofence-container">
                    <div id="map" className="kst-geofence-map" ref={h.mapContainerRef}></div>
                    <button type="button" className="kstc-btn kstc-btn--warning" onClick={h.handleResetGeoFence}>Reset Geo-Fence</button>
                  </div>
                )}
              </div>

              {/* Advanced Sub-tab */}
              <div style={{ display: h.tourStopSubTab === 'advanced' ? '' : 'none' }} className="kst-form-section">
                <h4>Advanced</h4>
                <p>Choose which days of the week this stop runs. You can optionally limit it further to only even- or odd-numbered dates of the month.</p>
                <div className="kstc-form-group">
                  <label className="kstc-label">Run this stop on:</label>
                  <div className="kst-day-presets" role="group" aria-label="Day presets">
                    {[
                      { label: 'Every day', mask: DAY_MASK_ALL },
                      { label: 'Weekdays', mask: DAY_MASK_WEEKDAYS },
                      { label: 'Weekends', mask: DAY_MASK_WEEKENDS },
                    ].map((preset) => {
                      const active = (h.currentTourStop.enabledDaysOfWeek ?? DAY_MASK_ALL) === preset.mask;
                      return (
                        <button
                          type="button"
                          key={preset.label}
                          className={`kst-day-preset ${active ? 'kst-day-preset--active' : ''}`}
                          aria-pressed={active}
                          onClick={() => h.setCurrentTourStop((prev: any) => ({ ...prev, enabledDaysOfWeek: preset.mask }))}
                        >{preset.label}</button>
                      );
                    })}
                  </div>
                  <div className="kst-day-toggles" role="group" aria-label="Days of the week">
                    {WEEK_DAYS.map((day) => {
                      const mask = h.currentTourStop.enabledDaysOfWeek ?? DAY_MASK_ALL;
                      const on = (mask & day.bit) !== 0;
                      return (
                        <button
                          type="button"
                          key={day.name}
                          className={`kst-day-toggle ${on ? 'kst-day-toggle--on' : ''}`}
                          aria-pressed={on}
                          aria-label={day.name}
                          title={day.name}
                          onClick={() => h.setCurrentTourStop((prev: any) => {
                            const cur = prev.enabledDaysOfWeek ?? DAY_MASK_ALL;
                            const isOn = (cur & day.bit) !== 0;
                            return { ...prev, enabledDaysOfWeek: isOn ? (cur & ~day.bit) : (cur | day.bit) };
                          })}
                        >{day.short}</button>
                      );
                    })}
                  </div>
                  {((h.currentTourStop.enabledDaysOfWeek ?? DAY_MASK_ALL) === 0) && (
                    <span className="kst-day-warning">Select at least one day.</span>
                  )}
                </div>
                <div className="kstc-form-group">
                  <label className="kstc-label">Dates of the month:</label>
                  <Dropdownlist
                    value={h.currentTourStop.enabledTourStopDaysType}
                    items={(window as any).SelectableEnabledTourStopDaysTypes}
                    labelKey="value"
                    valueKey="code"
                    placeholder="Select a date option"
                    sortItems={false}
                    searchable={false}
                    onChange={(val) => h.setCurrentTourStop((prev: any) => ({ ...prev, enabledTourStopDaysType: val }))}
                  />
                </div>
              </div>
            </div>

            {/* ═══ Task List Page ═══ */}
            <div style={{ display: h.pageShowString === 'showTaskList' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <div>
                  <h3 className="kst-page-title">Task List</h3>
                  <p className="kst-page-description">Task lists give you the ability to create a global list of tasks that you can associate with all tour stops, managed in one location.</p>
                </div>
              </div>
              <KstDataGrid
                columns={taskColumns}
                rows={h.rows}
                loading={h.tasksLoading}
                pager={h.tasksPager}
                headerbuttons={tasksHeaderButton}
                showcheckbox={true}
                mobiletitlefield="name"
                onGoToPage={h.loadTourTasks}
                onButtonCommand={h.handleTaskListCommand}
                onCellClick={h.handleTaskCellClick}
              />
            </div>

            {/* ═══ Edit Task Page ═══ */}
            <div style={{ display: h.pageShowString === 'showTask' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <div>
                  <h3 className="kst-page-title">{h.currentTask.name || 'New Task'}</h3>
                  <p className="kst-page-description">{h.currentTask.defaultDescription}</p>
                </div>
              </div>
              <div className="kst-form-section">
                <div className="kstc-form-group">
                  <label className="kstc-label">Name</label>
                  <input type="text" className="kstc-input" maxLength={255} value={h.currentTask.name || ''} onChange={(e) => h.setCurrentTask((prev: any) => ({ ...prev, name: e.target.value }))} />
                </div>
                <div className="kstc-form-group">
                  <label className="kstc-label">Description</label>
                  <input type="text" className="kstc-input" maxLength={255} value={h.currentTask.defaultDescription || ''} onChange={(e) => h.setCurrentTask((prev: any) => ({ ...prev, defaultDescription: e.target.value }))} />
                </div>
                <div className="kstc-form-group">
                  <label className="kstc-label">Task Completion Exceptions</label>
                  <MultiSelect
                    value={h.currentTask.tourExemptionIdList || []}
                    items={h.taskFormExemptions}
                    labelKey="name"
                    valueKey="id"
                    placeholder="Select exceptions"
                    loading={h.taskFormExemptionsLoading}
                    onChange={(values) => h.setCurrentTask((prev: any) => ({ ...prev, tourExemptionIdList: values }))}
                  />
                  <p className="kst-empty-hint">When assigned, officers can use these exceptions as an alternative to normal task completion.</p>
                </div>
                <div className="kst-form-actions">
                  <button type="button" className="kstc-btn kstc-btn--success" onClick={h.handleSaveTask}>Save</button>
                </div>
              </div>
            </div>

            {/* ═══ Edit Tour Stop Task Page ═══ */}
            <div style={{ display: h.pageShowString === 'showTourStopTask' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <div>
                  <h3 className="kst-page-title">{h.currentTourStopTask.name}</h3>
                  <p className="kst-page-description">{h.currentTourStopTask.description}</p>
                </div>
              </div>
              <div className="kst-form-section">
                <div className="kstc-form-group">
                  <label className="kstc-label">Name</label>
                  <input type="text" className="kstc-input" maxLength={255} value={h.currentTourStopTask.name || ''} disabled />
                </div>
                <div className="kstc-form-group">
                  <label className="kstc-label">Description</label>
                  <input type="text" className="kstc-input" maxLength={255} value={h.currentTourStopTask.description || ''} disabled />
                </div>

                {/* Time Sensitive Section */}
                <div className="kst-time-sensitive-section">
                  <h4>Time Sensitive</h4>
                  <p>Configure time windows when this task must be completed for each day of the week.</p>
                  <div className="kst-time-sensitive-grid">
                    {(h.currentTourStopTask.DaysOfWeek || []).map((day: any, index: number) => (
                      <div key={index} className="kst-time-sensitive-day">
                        <div className="kst-time-sensitive-day-header">
                          <h5>{day.value}</h5>
                          {day.TAGroups.length === 0 && (
                            <button type="button" className="kstc-btn kstc-btn--success kstc-btn--sm" onClick={() => h.handleAddTaskTimeSensitivePeriod(day)}>Add</button>
                          )}
                        </div>
                        {day.TAGroups.map((sensitiveTask: any, avIndex: number) => (
                          <div key={avIndex} className="kst-time-sensitive-row">
                            <TimePicker
                              value={sensitiveTask.startTimeDisplay ?? ''}
                              placeholder="Start Time"
                              onChange={(val) => { sensitiveTask.startTimeDisplay = val; h.setCurrentTourStopTask((prev: any) => ({ ...prev })); }}
                            />
                            <span className="kst-time-sensitive-to">to</span>
                            <TimePicker
                              value={sensitiveTask.endTimeDisplay ?? ''}
                              placeholder="End Time"
                              onChange={(val) => { sensitiveTask.endTimeDisplay = val; h.setCurrentTourStopTask((prev: any) => ({ ...prev })); }}
                            />
                            <button type="button" className="kstc-btn kstc-btn--danger kstc-btn--sm" onClick={() => {
                              h.handleRemoveTaskTimeSensitivePeriod(sensitiveTask.guid, day.dayOfWeek);
                              day.TAGroups = [];
                              h.setCurrentTourStopTask((prev: any) => ({ ...prev }));
                            }}>Remove</button>
                          </div>
                        ))}
                        {day.TAGroups.length === 0 && (
                          <div className="kst-time-sensitive-empty">No time restriction</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="kst-form-actions">
                  <button type="button" className="kstc-btn kstc-btn--primary" onClick={h.handleUseTourStopTask}>Done</button>
                  <button type="button" className="kstc-btn kstc-btn--secondary" onClick={h.handleCloseTourStopTask}>Cancel</button>
                </div>
              </div>
            </div>

            {/* ═══ Exception List Page ═══ */}
            <div style={{ display: h.pageShowString === 'showExemptionList' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <div>
                  <h3 className="kst-page-title">Exception List</h3>
                  <p className="kst-page-description">The exception list is an authorized list of reasons why a task was not performed or why it was late.</p>
                </div>
              </div>
              <KstDataGrid
                columns={tourExemptionColumns}
                rows={h.rows}
                loading={h.exemptionsLoading}
                pager={h.tourExemptionsPager}
                headerbuttons={tourExemptionHeaderButtons}
                showcheckbox={true}
                showstatustoggle={true}
                mobiletitlefield="name"
                onGoToPage={h.loadTourExemptions}
                onButtonCommand={h.handleExemptionListCommand}
                onStatusSwitch={h.handleSwitchTourExemptionStatus}
              />
            </div>

            {/* ═══ Edit Exception Page ═══ */}
            <div style={{ display: h.pageShowString === 'showExemption' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header"><h3 className="kst-page-title">Task Exception</h3></div>
              <div className="kst-form-section">
                <div className="kst-form-row">
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label" htmlFor="Name">Name</label>
                    <input type="text" id="Name" className="kstc-input" maxLength={255} value={h.currentTourExemption.name || ''} onChange={(e) => h.setCurrentTourExemption((prev: any) => ({ ...prev, name: e.target.value }))} />
                  </div>
                  <div className="kstc-form-group kst-form-col">
                    <label className="kstc-label" htmlFor="Description">Description</label>
                    <input type="text" id="Description" className="kstc-input" maxLength={255} value={h.currentTourExemption.description || ''} onChange={(e) => h.setCurrentTourExemption((prev: any) => ({ ...prev, description: e.target.value }))} />
                  </div>
                </div>
                <div className="kst-form-actions">
                  <button type="button" className="kstc-btn kstc-btn--success" onClick={h.handleSaveTourExemption}>Save</button>
                </div>
              </div>
            </div>

            {/* ═══ All Tour Logs Page ═══ */}
            <div style={{ display: h.pageShowString === 'showAllTourLogs' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">All Tour Logs</h3>
                <button type="button" className="kstc-btn kstc-btn--secondary" onClick={h.showGuardTourLogs}>Show Only Guard Logs</button>
              </div>
              <KstDataGrid
                columns={tourLogColumns}
                rows={h.rows}
                loading={h.tourLogsLoading}
                pager={h.tourLogsPager}
                showcheckbox={false}
                showheader={false}
                mobiletitlefield="entry"
                onGoToPage={h.loadAllTourLogs}
              />
            </div>

            {/* ═══ Guard Tour Logs Page ═══ */}
            <div style={{ display: h.pageShowString === 'showGuardTourLogs' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">Guard Tour Logs</h3>
                <button type="button" className="kstc-btn kstc-btn--secondary" onClick={h.showAllTourLogs}>Show All Logs</button>
              </div>
              <KstDataGrid
                columns={tourLogColumns}
                rows={h.rows}
                loading={h.tourLogsLoading}
                pager={h.tourLogsPager}
                showcheckbox={false}
                showheader={false}
                mobiletitlefield="entry"
                onGoToPage={h.loadGuardTourLogs}
              />
            </div>

            {/* ═══ Reminders Page (company-wide config) ═══ */}
            <div style={{ display: h.pageShowString === 'showReminders' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header"><h3 className="kst-page-title">Reminders</h3></div>
              <div className="kst-page-description">
                <h4>End-of-stop reminder email</h4>
                <p>Approximately 24 hours before each tour stop ends, an email is sent to the recipients below. This setting applies to <strong>all tour stops in your company</strong>. Leave blank to disable reminders company-wide.</p>
              </div>
              <div className="kst-form-section">
                <div className="kstc-form-group">
                  <label className="kstc-label" htmlFor="companyTourStopReminderEmail">Recipient email address(es)</label>
                  <div className="kst-reminder-email-row">
                    <EmailChipsInput
                      id="companyTourStopReminderEmail"
                      value={h.companyReminderEmail}
                      onChange={h.setCompanyReminderEmail}
                      placeholder="email@example.com"
                      maxAddresses={10}
                      maxLength={1000}
                    />
                    <small className="kstc-help-text">
                      Up to 10 recipients. Press Enter, Tab, or comma to add each address.
                    </small>
                  </div>
                </div>
                <div className="kst-form-actions">
                  <button type="button" className="kstc-btn kstc-btn--primary"
                          onClick={h.handleSaveCompanyReminderEmail}
                          disabled={h.companyReminderEmailLoading}>
                    Save
                  </button>
                </div>
              </div>
            </div>

            {/* ═══ Guard Tour History Page ═══ */}
            <div style={{ display: h.pageShowString === 'showGuardTourHistory' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header"><h3 className="kst-page-title">Tour History</h3></div>
              <KstDataGrid
                columns={guardTourHistoryColumns}
                rows={h.rows}
                loading={h.historyLoading}
                pager={h.guardTourHistoryPager}
                showcheckbox={false}
                showheader={false}
                mobiletitlefield="title"
                onGoToPage={h.loadGuardTourHistory}
              />
            </div>

            {/* ═══ Guard Tour Stops for Guard Tour Page ═══ */}
            <div style={{ display: h.pageShowString === 'showGuardTourStopsForGuardTour' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header"><h3 className="kst-page-title">Tour</h3></div>
              <KstDataGrid
                columns={guardTourStopsForGuardTourColumns}
                rows={h.rows}
                loading={h.guardTourStopsLoading}
                pager={h.guardTourStopsForGuardTourPager}
                showcheckbox={false}
                showheader={false}
                mobiletitlefield="title"
              />
            </div>

            {/* ═══ Guard Tour Stop Detail Page ═══ */}
            <div style={{ display: h.pageShowString === 'showGuardTourStop' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header"><h3 className="kst-page-title">Tour Stop</h3></div>
              <div className="kst-detail-section">
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Title</div>
                  <div className="kst-detail-value">{h.currentGuardTourStop.title}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Start Time</div>
                  <div className="kst-detail-value">{hasValue(h.currentGuardTourStop.activeStartDateTime) ? moment(h.currentGuardTourStop.activeStartDateTime).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : ''}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">End Time</div>
                  <div className="kst-detail-value">{hasValue(h.currentGuardTourStop.activeEndDateTime) ? moment(h.currentGuardTourStop.activeEndDateTime).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : ''}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Client</div>
                  <div className="kst-detail-value">{h.currentGuardTourStop.npsdClientName}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Sites</div>
                  <div className="kst-detail-value">{h.currentGuardTourStop.npsdClientSitesNames}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Location</div>
                  <div className="kst-detail-value">{h.currentGuardTourStop.location}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Instructions</div>
                  <div className="kst-detail-value">{h.currentGuardTourStop.instructions}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Tasks</div>
                  <div className="kst-detail-value">
                    {(h.currentGuardTourStop.tourStopTaskList || []).map((task: any, taskIdx: number) => (
                      <div key={taskIdx} className="kst-task-item">
                        <span className="kst-task-number">{task.listIdx + 1}) </span>
                        <span className="kst-task-name">{task.name}</span>
                        <div className="kst-task-description">{task.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">{h.officerLabel} Comments</div>
                  <div className="kst-detail-value">{h.currentGuardTourStop.areOfficerCommentsRequired === (window as any).OfficerCommentsRequired.REQUIRED ? 'Required' : (h.currentGuardTourStop.areOfficerCommentsRequired === (window as any).OfficerCommentsRequired.OPTIONAL ? 'Optional' : 'Disabled')}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Times to Complete</div>
                  <div className="kst-detail-value">{h.currentGuardTourStop.numTimesToComplete}</div>
                </div>
                <div className="kst-detail-row">
                  <div className="kst-detail-label">Completed</div>
                  <div className="kst-detail-value">{(h.currentGuardTourStop.completionList || []).length} times</div>
                </div>
                {hasValue(h.currentGuardTourStop.completionList) && h.currentGuardTourStop.completionList.length > 0 && (
                  <div className="kst-detail-row">
                    <div className="kst-detail-label">Completions</div>
                    <div className="kst-detail-value">
                      {h.currentGuardTourStop.completionList.map((completion: any, cidx: number) => (
                        <div key={cidx} className="kst-completion-item">
                          <div className="kst-completion-info">
                            <span className="kst-completion-number">{cidx + 1})</span>
                            <span className="kst-completion-name">{completion.completedByName}</span> -
                            <span className="kst-completion-time">{moment(completion.completedAt).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT)}</span>
                            <div className="kst-completion-comments">{completion.officerComments}</div>
                            <a className="kst-link" onClick={() => h.handleShowUpdateOfficerComments(completion.id, completion.officerComments, h.currentGuardTourStop.id)}>(Update Comments)</a>
                          </div>
                          {hasValue(completion.latitude) && hasValue(completion.longitude) && (
                            <div className="kst-completion-map">
                              <img src={h.getGoogleMapsImageSrcForCoordinates(completion.latitude, completion.longitude)} crossOrigin="anonymous" alt="Completion location" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ═══ Update Officer Comments Page ═══ */}
            <div style={{ display: h.pageShowString === 'updateOfficerComments' ? '' : 'none' }} className="kst-page">
              <div className="kst-page-header"><h3 className="kst-page-title">Update {h.officerLabel} Comments</h3></div>
              <div className="kst-form-section">
                <div className="kstc-form-group">
                  <textarea className="kstc-textarea" rows={15} value={h.currentUpdateOfficerComments.officerComments || ''} onChange={(e) => h.setCurrentUpdateOfficerComments((prev: any) => ({ ...prev, officerComments: e.target.value }))} />
                </div>
                <div className="kst-form-actions">
                  <button type="button" className="kstc-btn kstc-btn--secondary" onClick={() => h.showGuardTourStop(h.currentUpdateOfficerComments.guardTourStopId)}>Cancel</button>
                  <button type="button" className="kstc-btn kstc-btn--success" onClick={h.handleUpdateOfficerComment}>Update</button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ═══ Task-to-Stop Modals ═══ */}
      <AssociatedStopsModal
        open={h.associatedStopsModal.open}
        task={h.associatedStopsModal.task}
        onClose={h.handleCloseAssociatedStops}
        onAssignMore={h.handleAssignMoreFromAssociated}
        onChanged={h.handleAssociatedStopsChanged}
      />
      <AssignToStopsModal
        open={h.assignToStopsModal.open}
        tasks={h.assignToStopsModal.tasks}
        onClose={h.handleCloseAssignToStops}
        onComplete={h.handleAssignToStopsComplete}
      />
    </div>
  );
}

